import React, { useState, useEffect, useCallback } from "react";
import {
  Briefcase, Truck, Inbox, DollarSign, CheckCircle2, Star, Split, ExternalLink, Plus, Workflow, ClipboardList, PenSquare,
} from "lucide-react";
import { Modal } from "./Modal";
import { Badge, closeBlocker } from "./Badges";
import { Loader } from "./PageHead";
import { EquipmentModal } from "./EquipmentModal";
import { ActionModal } from "./WorklistActions";
import { AddLineModal } from "./AddLineModal";
import { EditLineModal } from "./EditLineModal";
import { RemoveLineModal } from "./RemoveLineModal";
import { ReopenLineModal } from "./ReopenLineModal";
import { ReturnToSubconModal } from "./ReturnToSubconModal";
import { SendToSubconModal } from "./SendToSubconModal";
import { MarkPaidModal } from "./MarkPaidModal";
import { BulkQuotePoModal } from "./BulkQuotePoModal";
import { QuoteManagerModal } from "./job-detail/AddQuoteModal";
import { UnserviceableModal } from "./UnserviceableModal";
import { UnserviceableReviewModal } from "./UnserviceableReviewModal";
import { DispatchCloseoutModal } from "./DispatchCloseoutModal";
import { JobHeaderCard, JobHeaderActions } from "./job-detail/JobHeaderCard";
import { ChangeJobCustomerModal } from "./job-detail/ChangeJobCustomerModal";
import { JobLinesTable } from "./job-detail/JobLinesTable";
import { JobSplitModal } from "./job-detail/JobSplitModal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const JOB_STATUS_META = {
  received: ["Received", "b-slate"],
  in_progress: ["In Progress", "b-blue"],
  on_hold: ["On Hold", "b-amber"],
  ready_for_admin: ["Ready for Admin", "b-blue"],
  dispatched: ["Ready for Collection", "b-green"],
  closed: ["Closed", "b-slate"],
  empty: ["No lines", "b-slate"],
};

// Lines already off-board (dispatched onward) must use their own per-line
// "Send to Subcon" action (ReturnToSubconModal) instead of this bulk one —
// that path correctly re-opens a closed/settled line rather than silently
// overwriting it.
const SUBCON_BULK_BLOCKED = new Set(["closed", "paid", "dispatched", "delivered", "collected"]);

// Statuses eligible for a technician's bulk "Send to Admin" (bench work is
// already recorded on every selected line, whatever mix of outcomes it
// took to get there) — mirrors Worklist.js's BULK_ADMIN_ELIGIBLE
// (SPT-PTE-2026-0028: a mixed batch of calibrated/adjusted/repaired items
// can now be closed out to Admin in one go from the full Job Detail Modal
// too, not just Worklist's inline job drill-down).
const TECH_BULK_ADMIN_ELIGIBLE = new Set(["calibrated", "adjusted", "repaired"]);
// Statuses eligible for a technician's bulk "Mark Calibrated" (SPT-PTE-2026-0034
// follow-up: extend the same fix from Worklist.js's inline drill-down to the
// full Job Detail Modal's technician bulk bar — a mixed selection of
// pre-outcome lines (In Progress, Assigned, Awaiting Spares, Awaiting Customer
// Approval, etc.) AND/OR already-outcome-recorded lines (Calibrated/Adjusted/
// Repaired) can all be bulk-marked Calibrated in one action).
const TECH_BULK_CALIBRATE_ELIGIBLE = new Set([
  "received", "assigned", "in_progress", "parts_required", "awaiting_spares",
  "parts_received", "awaiting_customer_approval", "awaiting_quote",
  "returned_from_subcontractor", "failed_calibration", "unable_to_calibrate",
  "calibrated", "adjusted", "repaired",
]);

// SPT-PTE-2026-0071 — lines with no quote yet form their own group.
const NO_QUOTE = "__no_quote__";
const quoteKey = (i) => i.quote_number || NO_QUOTE;

export function JobDetailModal({ jobId, onClose, onChanged }) {
  const { user, can } = useAuth();
  const canEdit = can("equipment", "edit");
  const canSplit = can("job_split", "edit");
  const canDispatch = can("dispatch", "edit");
  const isManager = user?.role === "manager" || user?.role === "admin";
  const isTechnician = user?.role === "technician";
  // Technicians get the simplified action picker even without equipment:edit.
  const canAct = canEdit || isTechnician;
  // Only non-technicians (with edit rights) see the dispatch/payment/admin bulk bar + row selection.
  const showSelect = canEdit && !isTechnician;

  const [job, setJob] = useState(null);
  const [editing, setEditing] = useState(false);
  const [hdr, setHdr] = useState({});
  const [expanded, setExpanded] = useState({}); // itemId -> bool
  const [selected, setSelected] = useState(new Set());
  const [action, setAction] = useState(null); // {key, item}
  const [openEquip, setOpenEquip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitNote, setSplitNote] = useState("");
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [changeCustomerOpen, setChangeCustomerOpen] = useState(false);
  const [navJobId, setNavJobId] = useState(jobId);
  const [addingLine, setAddingLine] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [removingLine, setRemovingLine] = useState(null);
  const [reopeningLine, setReopeningLine] = useState(null);
  const [returningToSubconLine, setReturningToSubconLine] = useState(null);
  const [markingPaidLine, setMarkingPaidLine] = useState(null);
  const [unserviceableId, setUnserviceableId] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [sendSubconOpen, setSendSubconOpen] = useState(false);
  const [quotePick, setQuotePick] = useState(() => new Set());
  const [quoteMgr, setQuoteMgr] = useState(null); // "add" | "edit"
  const [bulkQuotePoOpen, setBulkQuotePoOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/jobs/${navJobId}`);
      setJob(r.data);
      setHdr({
        contact_person: r.data.contact_person || "",
        internal_job_reference: r.data.internal_job_reference || "",
        notes_admin: r.data.notes_admin || "",
        end_user: !!r.data.end_user,
        end_user_name: r.data.end_user_name || "",
        end_user_email: r.data.end_user_email || "",
        end_user_phone: r.data.end_user_phone || "",
        end_user_company: r.data.end_user_company || "",
        end_user_address_line_1: r.data.end_user_address_line_1 || "",
        end_user_address_line_2: r.data.end_user_address_line_2 || "",
        end_user_city: r.data.end_user_city || "",
        end_user_province: r.data.end_user_province || "",
        end_user_country: r.data.end_user_country || "",
        end_user_postal_code: r.data.end_user_postal_code || "",
        end_user_address_unknown: !!r.data.end_user_address_unknown,
        end_user_address_unknown_reason: r.data.end_user_address_unknown_reason || "",
        quote_number: r.data.quote_number || "",
        delivery_note_number: r.data.delivery_note_number || "",
        date_received: (r.data.date_received || "").slice(0, 10),
      });
    } catch (e) { toast.error(apiErr(e)); }
  }, [navJobId]);

  useEffect(() => { load(); }, [load]);

  const doSplit = async () => {
    if (!selected.size) { toast.message("Select at least one line to split"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${navJobId}/split`, {
        item_ids: [...selected],
        notes: splitNote.trim() || undefined,
      });
      toast.success(`Split into ${r.data.new_delivery_note_number} (${r.data.moved_count} item${r.data.moved_count !== 1 ? "s" : ""})`);
      setSelected(new Set());
      setSplitOpen(false);
      setSplitNote("");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const jumpToFamily = (fid) => {
    if (fid === navJobId) return;
    setSelected(new Set());
    setExpanded({});
    setQuotePick(new Set());
    setNavJobId(fid);
  };

  const saveHeader = async () => {
    setBusy(true);
    try {
      await api.patch(`/jobs/${navJobId}/header`, hdr);
      toast.success("Header updated");
      setEditing(false);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (rows) => setSelected((s) => s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));

  const bulk = async (status) => {
    if (!selected.size) { toast.message("Select at least one line first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/job-items/bulk-status`, { item_ids: [...selected], status });
      toast.success(`${r.data.count} line${r.data.count !== 1 ? "s" : ""} → ${status.replace(/_/g, " ")}`);
      if (r.data.skipped?.length) toast.message(`${r.data.skipped.length} skipped`);
      setSelected(new Set());
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  // NOTE: "paid" is a payment STATE (payment_status field), not an item
  // operational status — /job-items/bulk-status's "paid" value only closes a
  // line that's ALREADY payment-satisfied (or no-ops if already closed), it
  // never actually records the payment. This mirrors Dispatch.js's own
  // correctly-wired "mark as paid" bulk option (SPT-PTE-2026-0030) so the Job
  // Detail Modal's bulk bar actually flips payment_status → "paid" (and
  // auto-closes the line if it's already handed over) instead of being a
  // silent no-op on an already-closed line.
  const bulkPayment = async (payment_status, itemIds) => {
    const ids = itemIds || [...selected];
    if (!ids.length) { toast.message("Select at least one line first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/job-items/bulk-payment`, { item_ids: ids, payment_status });
      toast.success(`${r.data.count} line${r.data.count !== 1 ? "s" : ""} → ${payment_status.replace(/_/g, " ")}`);
      if (r.data.skipped?.length) toast.message(`${r.data.skipped.length} skipped`);
      setSelected(new Set());
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  // Run the action chosen in a line's "Select action…" dropdown.
  const runLineAction = async (key, item) => {
    if (key === "start_work") {
      setBusy(true);
      try {
        await api.post(`/job-items/${item.id}/start`);
        toast.success("Work started — status set to In Progress");
        await load();
        onChanged && onChanged();
      } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
      return;
    }
    if (key === "unserviceable") { setUnserviceableId(item.id); return; }
    if (key === "back_to_progress" || key === "send_to_admin") {
      setBusy(true);
      try {
        const newStatus = key === "send_to_admin" ? "ready_for_admin" : "in_progress";
        await api.patch(`/job-items/${item.id}/status`, { status: newStatus });
        toast.success(key === "send_to_admin"
          ? "Sent to Admin — removed from the technician queue"
          : "Sent back to In Progress for rework");
        await load();
        onChanged && onChanged();
      } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
      return;
    }
    // calibrated / adjusted / repaired / parts_required / add_note → existing ActionModal
    setAction({ key, item });
  };

  // Manager/admin-only manual status correction (e.g. a line stuck on
  // "Received" when a tech was actually already assigned, or vice versa).
  const overrideStatus = async (item, status, note) => {
    setBusy(true);
    try {
      await api.patch(`/job-items/${item.id}/override-status`, { status, note });
      toast.success(`Status corrected to "${status.replace(/_/g, " ")}"`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!job) return <Modal open title="Loading…" onClose={onClose}><Loader /></Modal>;

  const [jsLabel, jsCls] = JOB_STATUS_META[job.job_status] || [job.job_status, "b-slate"];
  const items = job.items || [];
  // SPT-PTE-2026-0062 — when a customer has several concurrent quotations on
  // one job, each line can carry its own Sage Quote Number; only show the
  // tab filter once there's actually more than one distinct value to split.
  // SPT-PTE-2026-0071 — each quote (plus "Not yet quoted") is a GROUP on the
  // DN. Ticking one or more groups shows only their lines AND selects them,
  // so Close-out / Mark Paid / the bulk actions work on exactly that quote's
  // lines (several quotes can be combined, e.g. one PO covering many quotes).
  // With nothing ticked, the lines are listed group by group.
  const distinctQuotes = [...new Set(items.map((i) => i.quote_number).filter(Boolean))];
  const unquotedCount = items.filter((i) => !i.quote_number).length;
  const quoteGroups = [
    ...distinctQuotes.map((q) => ({ key: q, label: `Q ${q}`, count: items.filter((i) => i.quote_number === q).length })),
    ...(unquotedCount ? [{ key: NO_QUOTE, label: "Not yet quoted", count: unquotedCount }] : []),
  ];
  const groupOrder = (i) => quoteGroups.findIndex((g) => g.key === quoteKey(i));
  const activePick = [...quotePick].filter((k) => quoteGroups.some((g) => g.key === k));
  const visibleItems = activePick.length
    ? items.filter((i) => activePick.includes(quoteKey(i)))
    : (quoteGroups.length > 1 ? [...items].sort((a, b) => groupOrder(a) - groupOrder(b)) : items);
  const togglePick = (key) => {
    const next = new Set(activePick);
    if (next.has(key)) next.delete(key); else next.add(key);
    setQuotePick(next);
    if (showSelect) setSelected(new Set(items.filter((i) => next.has(quoteKey(i))).map((i) => i.id)));
  };
  const clearPick = () => { setQuotePick(new Set()); if (showSelect) setSelected(new Set()); };
  const itemsAllClosed = items.every((i) => ["closed", "paid"].includes(i.status));
  const itemsAnyDispatched = items.some((i) => ["dispatched", "delivered", "collected"].includes(i.status));
  const selectedItems = items.filter((it) => selected.has(it.id));
  const hasBlockedSubconSelected = selectedItems.some((it) => SUBCON_BULK_BLOCKED.has(it.status));
  const canTechBulkAdmin = selectedItems.length > 0 && selectedItems.every((it) => TECH_BULK_ADMIN_ELIGIBLE.has(it.status));
  const canTechBulkCalibrate = selectedItems.length > 0 && selectedItems.every((it) => TECH_BULK_CALIBRATE_ELIGIBLE.has(it.status));

  const footer = (
    <>
      <button className="btn ghost" onClick={onClose} data-testid="job-detail-close">Close</button>
      {isManager && itemsAnyDispatched && !itemsAllClosed && (
        <button className="btn ghost"
          onClick={() => bulkPayment("paid", items
            .filter((it) => ["dispatched", "delivered", "collected", "closed"].includes(it.status)
              && !["paid", "not_required"].includes(it.payment_status))
            .map((it) => it.id))}
          disabled={busy} data-testid="job-mark-paid">
          <DollarSign size={14} /> Mark all Paid
        </button>
      )}
    </>
  );

  return (
    <>
      <Modal open onClose={onClose} widest tall icon={Briefcase}
        title={`Job · ${job.delivery_note_number || job.id}`}
        subtitle={`${job.customer_number ? job.customer_number + " · " : ""}${job.customer_name || "—"} · ${job.item_count} line${job.item_count !== 1 ? "s" : ""}`}
        headerActions={
          <JobHeaderActions
            editing={editing} setEditing={setEditing}
            saveHeader={saveHeader} cancelEdit={() => { setEditing(false); load(); }}
            busy={busy} canEdit={canEdit} canDispatch={canDispatch} isTechnician={isTechnician}
            onOpenCloseout={() => setCloseoutOpen(true)} jobId={navJobId}
          />
        }
        footer={footer}>

        {/* status pill */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <Badge label={jsLabel} cls={jsCls} testid="job-status-pill" />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(job.status_breakdown || {}).map(([s, n]) => (
              <span key={s} className="tag-soft mono" data-testid={`job-breakdown-${s}`} style={{ fontSize: 11 }}>{s} × {n}</span>
            ))}
          </div>
        </div>

        {/* "What's needed to move this job off the Closed queue" callout */}
        {(() => {
          const needed = new Set();
          items.filter((i) => i.status === "closed").forEach((i) => {
            closeBlocker(i.status, i.payment_status, i.quote_number, i.purchase_order, i.invoice_number)
              .forEach((h) => needed.add(h));
          });
          if (needed.size === 0) return null;
          const order = ["Mark as Paid", "Add Quote #", "Add PO #", "Add Invoice #"];
          const list = order.filter((h) => needed.has(h));
          return (
            <div className="card pad" data-testid="job-finalize-banner"
              style={{ background: "#FBF3D6", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <ClipboardList size={16} style={{ color: "var(--gold-deep)" }} />
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>To move this job off the Closed queue &amp; complete it on the Equipment Register:</span>
              {list.map((h) => (
                <span key={h} className="badge b-amber" style={{ fontSize: 11 }} data-testid={`job-finalize-need-${h.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                  <span className="bd" /> {h}
                </span>
              ))}
              <span className="t-mut" style={{ fontSize: 11, width: "100%" }}>Tip: type <b>N/A</b> in a billing field if it doesn't apply.</span>
            </div>
          );
        })()}

        {/* Family / split lineage banner */}
        {job.family && job.family.length > 1 && (
          <div className="card pad" data-testid="job-family-banner"
            style={{ background: "linear-gradient(180deg, rgba(201,162,39,.06), transparent)", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Split size={15} style={{ color: "var(--gold-deep)" }} />
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>This job is part of a split delivery note family:</span>
            {job.family.map((f) => (
              <button
                key={f.id}
                className={f.is_current ? "badge b-gold" : "badge"}
                onClick={() => jumpToFamily(f.id)}
                data-testid={`job-family-${f.id}`}
                disabled={f.is_current}
                title={f.is_root ? "Original delivery note" : "Split delivery note"}
                style={{
                  cursor: f.is_current ? "default" : "pointer",
                  border: f.is_current ? undefined : "1px solid var(--line)",
                  background: f.is_current ? undefined : "var(--card-2)",
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}
              >
                {f.delivery_note_number}
                {f.is_root && <span style={{ fontSize: 10, opacity: 0.7 }}>· original</span>}
                {!f.is_current && <ExternalLink size={10} style={{ opacity: 0.6 }} />}
              </button>
            ))}
          </div>
        )}

        <JobHeaderCard
          job={job}
          hdr={hdr} setHdr={setHdr}
          editing={editing} setEditing={setEditing}
          saveHeader={saveHeader}
          cancelEdit={() => { setEditing(false); load(); }}
          busy={busy}
          canEdit={canEdit} canDispatch={canDispatch} isTechnician={isTechnician} isManager={isManager}
          onOpenCloseout={() => setCloseoutOpen(true)}
          onChangeCustomer={() => setChangeCustomerOpen(true)}
          jobId={navJobId}
        />

        {/* technician-only bulk bar — "Mark Calibrated" (any mix of
            pre-outcome or already-outcome-recorded lines) plus "Send to
            Admin" (once bench work is recorded on every selected line), so a
            mixed batch of calibrated/adjusted/repaired lines (each already
            actioned + noted individually via its own "Select action…") can
            be closed out to Admin in one go, instead of one line at a time
            (SPT-PTE-2026-0028, extended by SPT-PTE-2026-0034) */}
        {isTechnician && (
          <div className="card pad" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} data-testid="job-tech-bulk-bar">
            <span className="t-mut" style={{ fontSize: 12 }}>{selected.size} selected</span>
            <button className="btn sm gold" disabled={!canTechBulkCalibrate || busy} onClick={() => bulk("calibrated")}
              title="Mark all selected lines as Calibrated, even if they're in different statuses (In Progress, Awaiting Spares, Awaiting Customer Approval, Adjusted, Repaired, etc.)"
              data-testid="job-tech-bulk-calibrate">
              <CheckCircle2 size={13} /> Mark Calibrated
            </button>
            <button className="btn sm ghost" disabled={!canTechBulkAdmin || busy} onClick={() => bulk("ready_for_admin")}
              title="Select one or more calibrated/adjusted/repaired items to enable"
              data-testid="job-tech-bulk-admin">
              <Inbox size={13} /> Send to Admin
            </button>
            {selected.size > 0 && (
              <button className="btn sm ghost" onClick={() => setSelected(new Set())} data-testid="job-tech-bulk-clear">
                Clear
              </button>
            )}
          </div>
        )}

        {/* bulk actions bar — hidden from technicians (no dispatch/payment/admin) */}
        {showSelect && (
          <div className="card pad" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span className="t-mut" style={{ fontSize: 12 }}>{selected.size} selected</span>
            <button className="btn sm gold" disabled={!selected.size || busy} onClick={() => bulk("ready_for_delivery")} data-testid="job-bulk-ready">
              <Truck size={13} /> In Dispatch
            </button>
            <button className="btn sm ghost" disabled={!selected.size || busy} onClick={() => bulk("ready_for_admin")} data-testid="job-bulk-admin">
              <Inbox size={13} /> Send to Admin
            </button>
            <button className="btn sm ghost" disabled={!selected.size || busy} onClick={() => bulk("dispatched")} data-testid="job-bulk-dispatched">
              <CheckCircle2 size={13} /> Mark Ready for Collection
            </button>
            {isManager && (
              <button className="btn sm ghost" disabled={!selected.size || busy} onClick={() => bulkPayment("paid")} data-testid="job-bulk-paid">
                <DollarSign size={13} /> Mark Paid
              </button>
            )}
            {canEdit && (
              <button className="btn sm ghost" disabled={!selected.size || busy} onClick={() => setBulkQuotePoOpen(true)} data-testid="job-bulk-quote-po">
                <PenSquare size={13} /> Edit Quote # / PO Received
              </button>
            )}
            {canSplit && (
              <button
                className="btn sm ghost"
                disabled={!selected.size || busy || (selected.size >= (items.length || 0))}
                onClick={() => setSplitOpen(true)}
                data-testid="job-split-btn"
                title={selected.size >= (items.length || 0) ? "At least one item must remain on the original job" : "Move selected items into a new delivery note"}
              >
                <Split size={13} /> Split to new DN
              </button>
            )}
            <button
              className="btn sm ghost"
              disabled={!selected.size || busy || hasBlockedSubconSelected}
              onClick={() => setSendSubconOpen(true)}
              data-testid="job-bulk-send-subcon"
              title={hasBlockedSubconSelected
                ? "One or more selected lines are already closed/dispatched — use that line's own 'Send to Subcon' action instead"
                : "Send the selected line(s) to a subcontractor"}
            >
              <Workflow size={13} /> Send to Subcon
            </button>
            <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11.5 }} className="t-mut">
              <Star size={11} /> &quot;Closed&quot; auto-flips when every line is Ready for Collection / Delivered / Collected.
            </span>
          </div>
        )}

        {(quoteGroups.length > 1 || (showSelect && items.length > 0)) && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "4px 0 10px" }}
            data-testid="job-quote-groups">
            {quoteGroups.length > 1 && (
              <>
                <span className="t-mut" style={{ fontSize: 12 }}>Quotes on this DN:</span>
                <div className="ftabs" data-testid="job-quote-filter-tabs">
                  <button className={`ftab ${activePick.length === 0 ? "active" : ""}`}
                          style={{ padding: "4px 10px", fontSize: 12 }}
                          onClick={clearPick} data-testid="job-quote-filter-all">
                    All ({items.length})
                  </button>
                  {quoteGroups.map((g) => (
                    <button key={g.key} className={`ftab ${activePick.includes(g.key) ? "active" : ""}`}
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            onClick={() => togglePick(g.key)}
                            title="Show and select this quote's lines. Click more than one quote to combine them."
                            data-testid={`job-quote-filter-${g.key === NO_QUOTE ? "none" : g.key}`}>
                      {activePick.includes(g.key) ? "\u2713 " : ""}{g.label} ({g.count})
                    </button>
                  ))}
                </div>
              </>
            )}
            {showSelect && (
              <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
                <button className="btn sm ghost" onClick={() => setQuoteMgr("add")}
                  disabled={busy} data-testid="job-add-quote"
                  title="Link a Sage quote to the items it covers">
                  <Plus size={13} /> Add quote
                </button>
                {distinctQuotes.length > 0 && (
                  <button className="btn sm ghost" onClick={() => setQuoteMgr("edit")}
                    disabled={busy} data-testid="job-edit-quotes"
                    title="Move items between quotes, rename a quote or take it off items">
                    <PenSquare size={13} /> Edit quotes
                  </button>
                )}
              </span>
            )}
            {activePick.length > 0 && (
              <span className="t-mut" style={{ fontSize: 11.5, width: "100%" }} data-testid="job-quote-pick-hint">
                {showSelect
                  ? `${visibleItems.length} line(s) on the ticked quote(s) are selected. Use Close-out (top) or the actions above. Click All to go back.`
                  : "Showing only the ticked quote(s). Click All to go back."}
              </span>
            )}
          </div>
        )}

        <JobLinesTable
          items={visibleItems}
          expanded={expanded} setExpanded={setExpanded}
          selected={selected} toggle={toggle} toggleAll={toggleAll}
          showSelect={showSelect} isTechnician={isTechnician} isManager={isManager}
          canAct={canAct} canEdit={canEdit} canDispatch={canDispatch}
          busy={busy}
          onOpenEquip={setOpenEquip}
          onEditLine={setEditingLine}
          onRemoveLine={setRemovingLine}
          onReopenLine={setReopeningLine}
          onReturnToSubcon={setReturningToSubconLine}
          onMarkPaid={setMarkingPaidLine}
          onLineChanged={load}
          onRunAction={runLineAction}
          onReview={setReviewId}
          onOverrideStatus={overrideStatus}
        />

        {canEdit && (
          <div style={{ marginTop: 10 }}>
            <button className="btn ghost"
              style={{ width: "100%", justifyContent: "center", borderStyle: "dashed" }}
              onClick={() => setAddingLine(true)}
              data-testid="job-add-line">
              <Plus size={14} /> Add equipment line to this job
            </button>
          </div>
        )}
      </Modal>

      {addingLine && (
        <AddLineModal job={job}
          onClose={() => setAddingLine(false)}
          onSaved={() => { load(); onChanged && onChanged(); }} />
      )}
      {quoteMgr && (
        <QuoteManagerModal mode={quoteMgr} jobId={navJobId} headerQuote={job.quote_number || ""} items={items}
          onClose={() => setQuoteMgr(null)}
          onSaved={async () => { setQuotePick(new Set()); setSelected(new Set()); await load(); onChanged && onChanged(); }} />
      )}
      {bulkQuotePoOpen && (
        <BulkQuotePoModal itemIds={[...selected]}
          onClose={() => setBulkQuotePoOpen(false)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
      {editingLine && (
        <EditLineModal item={editingLine}
          onClose={() => setEditingLine(null)}
          onSaved={() => { load(); onChanged && onChanged(); }} />
      )}
      {removingLine && (
        <RemoveLineModal item={removingLine}
          onClose={() => setRemovingLine(null)}
          onRemoved={() => { load(); onChanged && onChanged(); }} />
      )}
      {reopeningLine && (
        <ReopenLineModal item={reopeningLine}
          onClose={() => setReopeningLine(null)}
          onReopened={() => { load(); onChanged && onChanged(); }} />
      )}
      {returningToSubconLine && (
        <ReturnToSubconModal item={returningToSubconLine}
          onClose={() => setReturningToSubconLine(null)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
      {sendSubconOpen && (
        <SendToSubconModal items={selectedItems}
          onClose={() => setSendSubconOpen(false)}
          onDone={() => { setSelected(new Set()); load(); onChanged && onChanged(); }} />
      )}
      {markingPaidLine && (
        <MarkPaidModal item={markingPaidLine}
          onClose={() => setMarkingPaidLine(null)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}

      {action && (
        <ActionModal action={action.key} item={action.item}
          onClose={() => setAction(null)}
          onDone={() => { load(); onChanged && onChanged(); }} />
      )}
      {unserviceableId && (() => {
        const usrItem = items.find((x) => x.id === unserviceableId);
        return (
          <UnserviceableModal itemId={unserviceableId}
            itemDescription={usrItem?.description}
            onClose={() => setUnserviceableId(null)}
            onDone={() => { setUnserviceableId(null); load(); onChanged && onChanged(); }} />
        );
      })()}
      {reviewId && (
        <UnserviceableReviewModal itemId={reviewId}
          onClose={() => setReviewId(null)}
          onChanged={() => { load(); onChanged && onChanged(); }} />
      )}
      {openEquip && (
        <EquipmentModal id={openEquip} canEdit={canEdit}
          onClose={() => setOpenEquip(null)}
          onChanged={() => load()} />
      )}

      {splitOpen && (
        <JobSplitModal
          job={job}
          selected={selected}
          splitNote={splitNote} setSplitNote={setSplitNote}
          busy={busy}
          onCancel={() => setSplitOpen(false)}
          onConfirm={doSplit}
        />
      )}

      {closeoutOpen && (
        <DispatchCloseoutModal jobId={navJobId}
          itemIds={showSelect && selected.size ? [...selected] : null}
          itemsSummary={showSelect && selected.size ? selectedItems : null}
          onClose={() => setCloseoutOpen(false)}
          onDone={() => { setCloseoutOpen(false); setSelected(new Set()); load(); onChanged && onChanged(); }} />
      )}

      {changeCustomerOpen && (
        <ChangeJobCustomerModal jobId={navJobId} currentName={job.customer_name}
          deliveryNoteNumber={job.delivery_note_number}
          onClose={() => setChangeCustomerOpen(false)}
          onDone={() => { setChangeCustomerOpen(false); load(); onChanged && onChanged(); }} />
      )}
    </>
  );
}
